import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ChevronRight, ChevronLeft, Check } from "lucide-react";
import jobLogo from "../images/Logo.svg";

/* ================= VALIDATION ================= */

const validationSchema = {
  0: yup.object({
    name: yup.string().required("Institution Name is required"),
    mobile: yup
      .string()
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
      .required("Mobile number is required"),
    email: yup.string().email("Invalid email format").required("Email is required"),
  }),
  1: yup.object({
    otp1: yup
      .string()
      .matches(/^[0-9]{6}$/, "OTP must be 6 digits")
      .required("OTP is required"),
  }),
  2: yup.object({
    password: yup
      .string()
      .min(6, "Minimum 6 characters")
      .matches(/[A-Z]/, "Must have at least one uppercase letter")
      .matches(/[a-z]/, "Must have at least one lowercase letter")
      .matches(/[0-9]/, "Must contain a number")
      .matches(/[!@#$%^&*]/, "Must have a special character")
      .required("Password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  }),
};

/* ================= COMPONENT ================= */

const InstitutionRegistrationForm = () => {
  const [step, setStep] = useState(0);
  const [tempToken, setTempToken] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [selectedType, setSelectedType] = useState("institution");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

  const initialValues = {
    name: "",
    mobile: "",
    email: "",
    otp1: "",
    password: "",
    confirmPassword: "",
  };

  const stepLabels = ["Contact Info", "Verification", "Password"];

  /* ================= HANDLERS ================= */

  const handleNext = async (values, validateForm, setErrors, setSubmitting) => {
    setSubmitting(true);
    try {
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);
        return;
      }

      if (step === 0) {
        const response = await axios.post(`${REACT_APP_API_URL}/api/auth/register-inst`, {
          name: values.name,
          mobile_number: values.mobile,
          email: values.email,
        });

        if (response.data.message === "OTP sent successfully") {
          setStep(1);
        } else {
          setErrors({ api: response.data.message || "Registration failed" });
        }
      }

      if (step === 1) {
        const response = await axios.post(`${REACT_APP_API_URL}/api/auth/verify-otp-inst`, {
          otp1: values.otp1,
        });
        setTempToken(response.data.token);
        setStep(2);
      }
    } catch (err) {
      setErrors({ api: err.response?.data?.message || "An error occurred" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values, actions) => {
    actions.setSubmitting(true);
    try {
      await axios.post(
        `${REACT_APP_API_URL}/api/auth/password-inst`,
        { password: values.password },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      alert("Registration complete! You can now log in.");
      navigate("/login");
    } catch (err) {
      actions.setErrors({ api: err.response?.data?.message || "An error occurred" });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleResendOtp = async (email, setErrors) => {
    setResendDisabled(true);
    try {
      const res = await axios.post(`${REACT_APP_API_URL}/api/auth/resend-otp`, { email });
      alert(res.data.message);
    } catch (err) {
      setErrors({ api: err.response?.data?.message || "Failed to resend OTP" });
    }

    let time = 30;
    setCountdown(time);
    const timer = setInterval(() => {
      time--;
      setCountdown(time);
      if (time <= 0) {
        clearInterval(timer);
        setResendDisabled(false);
      }
    }, 1000);
  };

  /* ================= UI ================= */

  return (
    <div
      className="min-h-screen w-full font-['Inter',sans-serif] flex items-center justify-center p-4 md:py-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >

      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
        />
      </div>

      {/* Card */}
      <div
        className="relative w-full !max-w-lg bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 z-10"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Branding */}
        <div className="flex flex-col items-center mb-6">
          <img src={jobLogo} alt="Logo" className="h-16 mb-4 cursor-pointer" onClick={() => navigate("/")} />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Register Institution</h1>
          <p className="text-gray-500 text-sm mt-1">Hire the best teaching talent</p>
        </div>

        {/* Register Type Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            type="button"
            onClick={() => {
              setSelectedType("user");
              navigate("/user");
            }}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${selectedType === "user"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Register as User
          </button>
          <button
            type="button"
            onClick={() => {
              setSelectedType("institution");
              navigate("/inst");
            }}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 border-none cursor-pointer ${selectedType === "institution"
              ? "bg-white text-blue-600 shadow-md"
              : "bg-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Register as Institution
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8 px-2">
          {stepLabels.map((label, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${step > i
                    ? "bg-green-500 text-white"
                    : step === i
                      ? "text-white shadow-lg"
                      : "bg-gray-200 text-gray-500"
                    }`}
                  style={step === i ? { background: 'linear-gradient(135deg, #667eea, #764ba2)' } : {}}
                >
                  {step > i ? <Check size={16} /> : i + 1}
                </div>
                <span className={`text-xs mt-1.5 font-medium ${step >= i ? "text-gray-700" : "text-gray-400"
                  }`}>
                  {label}
                </span>
              </div>
              {i < stepLabels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-5 rounded transition-all duration-300 ${step > i ? "bg-green-400" : "bg-gray-200"
                  }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema[step]}
          onSubmit={(values, actions) => {
            step === 2
              ? handleSubmit(values, actions)
              : handleNext(values, actions.validateForm, actions.setErrors, actions.setSubmitting);
          }}
        >
          {({ values, errors, isSubmitting, setErrors }) => (
            <Form>
              {errors.api && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4 text-center">
                  {errors.api}
                </div>
              )}

              {step === 0 &&
                [
                  ["name", "Institution Name", "Enter institution name"],
                  ["mobile", "Mobile Number", "Enter 10-digit mobile number"],
                  ["email", "Email Address", "Enter email address"],
                ].map(([field, label, placeholder]) => (
                  <div className="mb-4" key={field}>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      {label}
                    </label>
                    <Field
                      name={field}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      placeholder={placeholder}
                    />
                    <ErrorMessage name={field} component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
                  </div>
                ))}

              {step === 1 && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Enter OTP
                  </label>
                  <Field
                    name="otp1"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 text-center tracking-[0.5em] text-lg font-mono"
                    placeholder="• • • • • •"
                    maxLength={6}
                  />
                  <ErrorMessage name="otp1" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />

                  <button
                    type="button"
                    onClick={() => handleResendOtp(values.email, setErrors)}
                    disabled={resendDisabled}
                    className="mt-3 bg-transparent text-blue-600 border-none cursor-pointer text-sm font-medium hover:text-blue-700 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {resendDisabled ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                  </button>
                </div>
              )}

              {step === 2 && (
                <>
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Field
                        name="password"
                        type={showPassword ? "text" : "password"}
                        className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        placeholder="Create a strong password"
                      />
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                    </div>
                    <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Field
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                    </div>
                    <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                {step > 0 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 py-3 text-gray-600 font-semibold text-sm rounded-xl border-2 border-gray-200 bg-white cursor-pointer transition-all duration-300 hover:bg-gray-50 hover:border-gray-300 flex items-center justify-center gap-1"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${step > 0 ? "flex-1" : "w-full"} py-3.5 text-white font-semibold text-sm rounded-xl border-none cursor-pointer transition-all duration-300 flex items-center justify-center gap-1 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed`}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {step < 2 ? (
                    <>{isSubmitting ? "Processing..." : "Next"} <ChevronRight size={16} /></>
                  ) : (
                    <>{isSubmitting ? "Registering..." : "Complete Registration"}</>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {/* Login link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Already have an account?{" "}
            <span
              className="text-blue-600 cursor-pointer font-semibold hover:text-blue-700 transition-colors"
              onClick={() => navigate("/login")}
            >
              Sign In
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InstitutionRegistrationForm;
